import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@clerk/react";
import { Coins, Gift, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { makeApi } from "@/lib/api";
import { useT } from "@/i18n";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const TX = {
  es: {
    title: "Monedas de premio", goal: "¡Junta 500 monedas y gana 1 mes gratis del Plan Familia!",
    ways: ["+10 por cada lección terminada", "+5 por entrar cada día", "+5 por crear nombres de negocio, imágenes o dibujos con IA", "+5 al compartir tu app"],
    cap: "Hoy ganaste", capOf: "de 50 monedas extra", kid: "¡Ya tienes 500! Pide a tus papás que lo canjeen en el panel de padres.",
    redeem: "Canjear 500 monedas", confirmT: "¿Canjear 500 monedas?", confirmFree: "Tu familia recibe 1 mes gratis del Plan Familia.",
    confirmPaid: "Se descontará $4.98 de tu próximo cobro del Plan Familia.", yes: "Sí, canjear", no: "Cancelar",
    okFree: "¡Listo! 1 mes gratis del Plan Familia activado.", okPaid: "¡Listo! Tu próximo mes sale gratis.", fail: "No se pudo canjear, intenta otra vez",
    until: "Mes gratis activo hasta", missing: "Faltan", gate: "Solo para adultos, responde:", wrong: "Respuesta incorrecta",
    cancelling: "Tu suscripción ya está cancelada; canjea cuando la reactives o al terminar.", nosub: "No encontramos tu suscripción. Escríbenos para ayudarte.",
  },
  en: {
    title: "Reward coins", goal: "Collect 500 coins and win 1 free month of the Family Plan!",
    ways: ["+10 for each finished lesson", "+5 for visiting every day", "+5 for creating business names, images or drawings with AI", "+5 for sharing your app"],
    cap: "Today you earned", capOf: "of 50 extra coins", kid: "You have 500! Ask your parents to redeem it on the parents page.",
    redeem: "Redeem 500 coins", confirmT: "Redeem 500 coins?", confirmFree: "Your family gets 1 free month of the Family Plan.",
    confirmPaid: "$4.98 will be taken off your next Family Plan charge.", yes: "Yes, redeem", no: "Cancel",
    okFree: "Done! 1 free month of the Family Plan is active.", okPaid: "Done! Your next month is free.", fail: "Couldn't redeem, try again",
    until: "Free month active until", missing: "Missing", gate: "Adults only, answer:", wrong: "Wrong answer",
    cancelling: "Your subscription is already cancelled; redeem once it ends or after reactivating.", nosub: "We couldn't find your subscription. Contact us for help.",
  },
};

const rnd = () => 3 + Math.floor(Math.random() * 7);
const rndBig = () => 12 + Math.floor(Math.random() * 8);

export default function CoinRewards({ parent = false, onChange }) {
  const { getToken } = useAuth();
  const { lang } = useT();
  const tx = TX[lang] || TX.es;
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState([rndBig(), rnd()]);
  const [answer, setAnswer] = useState("");
  const load = useCallback(() => makeApi(getToken).get("/rewards").then((x) => setR(x.data)).catch(() => {}), [getToken]);
  useEffect(() => {
    load();
    window.addEventListener("coins-changed", load);
    return () => window.removeEventListener("coins-changed", load);
  }, [load]);
  if (!r) return null;

  const pct = Math.min(100, Math.round((r.coins / r.goal) * 100));
  const ready = r.coins >= r.goal;
  const paid = r.plan === "family" && r.plan_source !== "coins";
  const redeem = async (e) => {
    if (Number(answer) !== q[0] * q[1]) { e.preventDefault(); toast.error(tx.wrong); return; }
    setBusy(true);
    try {
      const x = await makeApi(getToken).post("/rewards/redeem");
      toast.success(x.data.kind === "stripe_credit" ? tx.okPaid : tx.okFree);
      await load();
      onChange?.();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(d === "subscription_cancelling" ? tx.cancelling : d === "subscription_not_found" ? tx.nosub : tx.fail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-white/85 backdrop-blur-sm border-4 border-yellow-300 rounded-[2rem] shadow-xl p-5 sm:p-7" data-testid="coin-rewards">
      <div className="flex flex-wrap items-center gap-3">
        <span className="bg-yellow-300 rounded-2xl p-2.5 shadow"><Coins className="w-7 h-7 text-amber-700" /></span>
        <h2 className="font-fredoka text-2xl sm:text-3xl font-bold text-blue-950">{tx.title}</h2>
        <span className="ml-auto font-fredoka text-2xl font-bold text-amber-700" data-testid="coin-rewards-count">{r.coins} / {r.goal}</span>
      </div>
      <p className="mt-1 font-bold text-blue-900 inline-flex items-center gap-1.5"><Gift className="w-5 h-5 text-pink-500" /> {tx.goal}</p>
      <div className="mt-3 h-6 rounded-full bg-yellow-100 border-2 border-yellow-300 overflow-hidden" data-testid="coin-rewards-bar">
        <div className="h-full bg-gradient-to-r from-yellow-300 to-amber-500 transition-[width] duration-700" style={{ width: `${pct}%` }} />
      </div>
      <p className="mt-1 text-sm font-semibold text-blue-900/80">
        {ready ? "" : `${tx.missing} ${r.goal - r.coins}. `}{tx.cap} {r.today} {tx.capOf}
      </p>
      {r.plan_source === "coins" && r.plan === "family" && r.plan_until && (
        <p className="mt-2 bg-lime-200 rounded-full px-4 py-1.5 font-bold text-green-900 w-fit" data-testid="coin-rewards-until">
          {tx.until} {new Date(r.plan_until).toLocaleDateString(lang === "en" ? "en-US" : "es-ES")}
        </p>
      )}
      <ul className="mt-3 grid sm:grid-cols-2 gap-1.5">
        {tx.ways.map((w) => <li key={w} className="text-sm font-semibold text-blue-950 flex gap-2"><Coins className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />{w}</li>)}
      </ul>
      {ready && !parent && <p className="mt-3 bg-pink-100 rounded-2xl px-4 py-2 font-bold text-pink-900" data-testid="coin-rewards-ask-parent">{tx.kid}</p>}
      {parent && (
        <AlertDialog>
          <AlertDialogTrigger asChild onClick={() => { setQ([rndBig(), rnd()]); setAnswer(""); }}>
            <button type="button" disabled={!ready || busy} className="mt-4 glossy-btn bg-blue-600 text-white font-fredoka font-bold text-lg px-6 py-2.5 rounded-full inline-flex items-center gap-2 disabled:opacity-50" data-testid="coin-rewards-redeem-btn">
              {busy ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Gift className="w-5 h-5" />} {tx.redeem}
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent data-testid="coin-rewards-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>{tx.confirmT}</AlertDialogTitle>
              <AlertDialogDescription>{paid ? tx.confirmPaid : tx.confirmFree}</AlertDialogDescription>
            </AlertDialogHeader>
            <div className="flex items-center gap-2 bg-rose-50 rounded-2xl p-3" data-testid="coin-rewards-gate">
              <span className="font-bold text-rose-800 text-sm">{tx.gate}</span>
              <span className="font-fredoka text-xl font-bold text-blue-950" data-testid="coin-rewards-gate-question">{q[0]} × {q[1]} =</span>
              <input value={answer} onChange={(ev) => setAnswer(ev.target.value.replace(/\D/g, "").slice(0, 3))} inputMode="numeric" className="w-20 border-4 border-sky-100 rounded-xl px-2 py-1 font-bold" data-testid="coin-rewards-gate-answer" />
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="coin-rewards-confirm-no">{tx.no}</AlertDialogCancel>
              <AlertDialogAction onClick={redeem} disabled={!answer} data-testid="coin-rewards-confirm-yes">{tx.yes}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </section>
  );
}
